import SocialService from "~/components/social/SocialService"
import DEFAULT_I18N from "~/i18n"
import { ellipsis, ucfirst } from "~/lib/utils"
import CommunicationService from "./CommunicationService"

// In this list we store the default URL to use to list the posts. It includes the query with the default sorting and filters to be used in all lists
export const DEFAULT_COMMUNICATION_LIST_ID = "posts"

export function getListId(channelId) {
    return channelId !== null
        ? `communication-${channelId}-posts`
        : "communication-all-posts"
}

function getUrl(channelId, query = "") {
    const url =
        channelId !== null
            ? `/communication-channels/${channelId}/posts`
            : "/communication-posts"
    if (query) {
        return `${url}?${query}`
    } else {
        return url
    }
}

export const getters = {
    getPost:
        (_state, _getters, rootState) =>
        ({ postId }) => {
            const instance = rootState.lists.instances[getListId(null)]
            if (!instance) return null
            return instance.items.find(p => p.postId === postId)
        },

    getPosts:
        (_state, _getters, rootState) =>
        ({ channelId = null }) => {
            const instance = rootState.lists.instances[getListId(channelId)]
            if (!instance) return []
            return instance.items
        },

    getChannels: state => {
        return state.channels
    },

    getTools:
        state =>
        ({ channelId = null }) => {
            if (!state.tools || state.tools.length == 0) {
                return []
            }
            if (channelId === null) {
                return state.tools
            }
            return state.tools.filter(tool => tool.channelId === channelId)
        },

    query:
        (_state, _getters, rootState) =>
        ({ channelId = null }) => {
            const listId = getListId(channelId)
            const instance = rootState.lists.instances[listId]
            return instance?.filters?.free_search ?? ""
        },
}

export const mutations = {
    setChannels(state, { channels }) {
        channels.forEach(c => {
            const channelIndex = state.channels.findIndex(
                ch => ch.channelId == c.channelId
            )
            if (channelIndex > -1) {
                state.channels[channelIndex] = Object.assign(
                    state.channels[channelIndex],
                    c
                )
            } else {
                state.channels.push(c)
            }
        })
    },

    setTools(state, { tools }) {
        state.tools = tools
    },

    clearModule(state) {
        Object.assign(state, {
            tools: [],
            channels: [],
        })
    },
}

export function makeActions({ service, socialService, i18n }) {
    i18n = i18n || DEFAULT_I18N.global
    return {
        clearFilters({ dispatch }, { channelId }) {
            const listId = getListId(channelId)
            dispatch("lists/clearFilters", { key: listId }, { root: true })
        },

        initList({ rootState, commit, dispatch }, { channelId }) {
            const listId = getListId(channelId)
            // No need to initialise the list twice
            if (rootState.lists.descriptions[listId]) return

            let query = ""
            const defaultDescription =
                rootState.lists.descriptions[DEFAULT_COMMUNICATION_LIST_ID]
            if (defaultDescription) {
                const { url } = defaultDescription
                query = url.split("?")[1]
            }

            const description = {
                cursor: undefined,
                listId,
                url: getUrl(channelId, query),
                itemsType: "post-card",
                title: "",
                presenter: "default",
            }
            commit("lists/setDescription", description, { root: true })
            dispatch("lists/init", { key: listId }, { root: true })
        },

        loadMore({ dispatch }, { channelId }) {
            dispatch(
                "lists/loadMore",
                {
                    key: getListId(channelId),
                },
                { root: true }
            )
        },

        async refreshPosts({ dispatch }, { channelId }) {
            dispatch(
                "lists/justRefresh",
                {
                    key: getListId(channelId),
                },
                { root: true }
            )
        },

        async refreshChannels({ commit }) {
            let channels = await service.getChannels()

            if (channels.length > 1) {
                channels.unshift({
                    channelId: null,
                    channelName: i18n.t("common.all", 2),
                })
            } else {
                channels = [
                    {
                        channelId: null,
                        channelName: i18n.t("common.all", 2),
                    },
                ]
            }

            commit("setChannels", {
                channels,
            })
        },

        async refreshPost({ dispatch }, { postId }) {
            await dispatch("initList", { channelId: null })
            const post = await service.getPost(postId)
            await dispatch("initList", { channelId: post.channelId })
            dispatch("updatePost", { post })
        },

        setFilters({ dispatch }, { channelId, filters }) {
            const listId = getListId(channelId)
            dispatch(
                "lists/setFilters",
                {
                    key: listId,
                    filters,
                },
                { root: true }
            )
        },

        setOrderBy({ dispatch }, { channelId, orderBy }) {
            const listId = getListId(channelId)
            dispatch(
                "lists/setOrderBy",
                {
                    key: listId,
                    orderBy,
                },
                { root: true }
            )
        },

        toggleDirection({ dispatch }, { channelId }) {
            const listId = getListId(channelId)
            dispatch(
                "lists/toggleDirection",
                {
                    key: listId,
                },
                { root: true }
            )
        },

        updatePost({ commit }, { post }) {
            const listKey = getListId(post.channelId)

            try {
                commit(
                    "lists/updateItem",
                    {
                        item: post,
                        listKey: "communication-all-posts",
                    },
                    { root: true }
                )
            } catch (_) {
                //do nothing
            }

            try {
                commit(
                    "lists/updateItem",
                    {
                        item: post,
                        listKey,
                    },
                    { root: true }
                )
            } catch (_) {
                //do nothing
            }
        },

        async refreshTools({ commit }) {
            const tools = await service.getTools()
            commit("setTools", {
                tools,
            })
        },

        /**
         * Social actions
         */
        async sendLike({ getters, dispatch }, { postId }) {
            const originalPost = getters.getPost({ postId })
            if (!originalPost) {
                throw new Error(`Post ${postId} doesn't exist`)
            }

            const { liked, likes } = originalPost.social
            const social = Object.assign({}, originalPost.social, {
                liked: !liked,
                likes: liked ? likes - 1 : likes + 1,
            })
            const newPost = Object.assign({}, originalPost, { social })
            dispatch("updatePost", {
                post: newPost,
            })

            try {
                const updatedPost = await socialService.likeResource(newPost)
                dispatch("updatePost", {
                    post: updatedPost,
                })
            } catch {
                dispatch("updatePost", {
                    post: originalPost,
                })
            }
        },

        async sendBookmark({ getters, dispatch }, { postId }) {
            const originalPost = Object.assign({}, getters.getPost({ postId }))
            if (!originalPost) {
                throw new Error(`Post ${postId} doesn't exist`)
            }

            const { bookmarked } = originalPost.social
            const social = Object.assign({}, originalPost.social, {
                bookmarked: !bookmarked,
            })
            const newPost = Object.assign({}, originalPost, { social })
            dispatch("updatePost", {
                post: newPost,
            })

            try {
                const updatedPost =
                    await socialService.bookmarkResource(newPost)
                dispatch("updatePost", {
                    post: updatedPost,
                })
                const info = updatedPost.social.bookmarked
                    ? i18n.t("common.wasAddedToBookmarks")
                    : i18n.t("common.wasRemovedFromBookmarks")

                dispatch(
                    "pushNotification",
                    {
                        title: ucfirst(ellipsis(updatedPost.title, 75)),
                        info: ucfirst(info),
                        type: updatedPost.social.bookmarked
                            ? "success"
                            : "info",
                    },
                    { root: true }
                )
            } catch {
                dispatch("updatePost", {
                    post: originalPost,
                })
            }
        },

        async updateCommentsCount(
            { getters, dispatch },
            { postId, commentsCount }
        ) {
            const post = getters.getPost({ postId })

            if (!post) {
                throw new Error(`Post ${postId} doesn't exist`)
            }

            const social = Object.assign({}, post.social, { commentsCount })
            const newPost = Object.assign({}, post, { social })

            dispatch("updatePost", {
                post: newPost,
            })
        },

        async accessPost({ getters, dispatch, rootGetters }, { postId }) {
            if (!rootGetters.currentUser) return
            const post = getters.getPost({ postId })
            if (!post) {
                throw new Error(`Post ${postId} doesn't exist`)
            }

            if (post.accessed) return

            await service.access(postId)
            dispatch("refreshPost", { postId })
        },
    }
}

export default {
    namespaced: true,
    state: {
        channels: [],
    },
    getters,
    mutations,
    actions: makeActions({
        service: new CommunicationService(),
        socialService: new SocialService(),
    }),
}
